import React from 'react';

import styled from 'styled-components';
import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import Shot, { ShotsList, ShotsListItem, ShotsDivider } from './shot';
import { PublicTitle, Question, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import ImageGallery from '../../image-gallery';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import { exercisePropTypes, exerciseDefaultProps } from '../../../constants/exercise';
import Geogebra from '../../geogebra';
import Stack from '../../stack';
import MathContent from '../../math-content';

export const StyledShot = styled(Shot)`
    display: inline-block;
    vertical-align: top;

    margin-left: var(--space-unit);

    font-weight: normal;
`;

export const handleClick = (exerciseId, shotId, answerId, answers, onAnswer) => {
    const isSelected = answers.find((item) => item.shotId === shotId && item.answerId === answerId);
    const updatedAnswers = answers.filter((item) => item.shotId !== shotId);
    !isSelected && updatedAnswers.push({ shotId: shotId, answerId: answerId });
    onAnswer(exerciseId, updatedAnswers);
};

export const handleValidation = (shot, answers) => {
    let match = get(
        answers.find((item) => item.shotId === shot.id),
        'answerId',
    );
    return match === undefined ? 'missing' : match === shot.idRef ? 'success' : 'error';
};

const Barrage = ({
    exercise,
    answers = exerciseDefaultProps.answers,
    onAnswer,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
}) => {
    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Show when={show.prompt && exercise.prompt}>
                <FluidContainer paddingBottom={24}>
                    <MathContent>{exercise.prompt}</MathContent>
                </FluidContainer>
            </Show>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>

                <div>
                    <Question style={{ lineHeight: '1.5rem' }}>{exercise.question}</Question>
                    <ShotsList>
                        {exercise.shots.map((shot, index) => (
                            <ShotsListItem key={index}>
                                <StyledShot
                                    answers={exercise.options}
                                    badgeIndex={index + 1}
                                    correctAnswerId={shot.idRef}
                                    handleClick={(shotId, answerId) => handleClick(exercise.id, shotId, answerId, answers, onAnswer)}
                                    id={shot.id}
                                    isValidated={!!correctionStatus}
                                    selectedAnswerId={get(
                                        answers.find((item) => item.shotId === shot.id),
                                        'answerId',
                                    )}
                                    text={shot.text}
                                    validationType={handleValidation(shot, answers)}
                                />
                                {index < exercise.shots.length - 1 && <ShotsDivider />}
                            </ShotsListItem>
                        ))}
                    </ShotsList>
                </div>
            </GridContainer>
        </>
    );
};

Barrage.propTypes = exercisePropTypes;

export default Barrage;
